import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Sparkline } from '@/components/dashboard/Sparkline';
import { RiyalIcon } from '@/lib/currency';

/**
 * Tiny indicator: ▲ +12.3% (success) or ▼ -4.1% (error). `invert=true`
 * flips the colour semantic — used for "expenses going down is good".
 */
export const ChangeIndicator = ({ current, previous, invert = false }) => {
  if (!previous && previous !== 0) return null;
  const change = !previous || previous === 0
    ? (current > 0 ? 100 : 0)
    : ((current - previous) / Math.abs(previous) * 100).toFixed(1);
  const isUp = parseFloat(change) > 0;
  const isGood = invert ? !isUp : isUp;
  return (
    <span className={`inline-flex items-center text-xs font-medium ml-2 ${isGood ? 'text-success' : 'text-error'}`}>
      {isUp ? '▲' : '▼'} {Math.abs(change)}%
    </span>
  );
};

export const PctBadge = ({ value }) => (
  value > 0 ? <span className="text-xs text-muted-foreground ml-1">({value}% of sales)</span> : null
);

/**
 * Main KPI stats grid (Sales / Expenses / Net Profit / etc.). Each card
 * renders its KPI + sparkline + change vs prior period + today-vs-yesterday
 * micro-stat + optional sub-items breakdown.
 */
export const StatCardsGrid = ({ statCards = [], todayVsYest = null, fmtAmt }) => {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {statCards.map((card) => {
        const Icon = card.icon;
        const rawVal = card.numValue || 0;
        const tvyKey = card.title === 'Total Sales'
          ? 'sales'
          : card.title === 'Total Expenses'
            ? 'expenses'
            : card.title === 'Net Profit'
              ? 'profit'
              : null;
        const tvyChange = todayVsYest && tvyKey ? todayVsYest.change[tvyKey] : null;
        const tvyToday = todayVsYest && tvyKey ? todayVsYest.today[tvyKey] : null;
        return (
          <Card
            key={card.title}
            className={`stat-card border-border ${card.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            data-testid={card.testId}
            onClick={card.link ? () => navigate(card.link) : undefined}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
              <div className={`${card.bgColor} p-2 rounded-lg`}>
                <Icon className={`h-5 w-5 ${card.color}`} strokeWidth={2} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl sm:text-3xl font-bold font-outfit inline-flex items-center gap-1" data-testid={`${card.testId}-value`}>
                    <RiyalIcon size="0.7em" /> {fmtAmt(rawVal)}
                  </span>
                </div>
                {card.sparkline && card.sparkline.length > 1 && (
                  <Sparkline data={card.sparkline} color={card.sparkColor} />
                )}
              </div>
              <div className="flex items-center mt-1 flex-wrap gap-1">
                {card.prev !== undefined && (
                  <ChangeIndicator current={rawVal} previous={card.prev} invert={card.invert} />
                )}
                {card.pct !== undefined && card.pct > 0 && <PctBadge value={card.pct} />}
              </div>
              {tvyChange !== null && (
                <div className="mt-2 pt-2 border-t border-border flex items-center justify-between" data-testid={`tvy-${tvyKey}`}>
                  <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                    Today: <RiyalIcon size="0.85em" /> {tvyToday?.toLocaleString()}
                  </span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    tvyChange > 0 && !card.invert
                      ? 'bg-success/20 text-success'
                      : tvyChange < 0 && !card.invert
                        ? 'bg-error/20 text-error'
                        : tvyChange > 0 && card.invert
                          ? 'bg-error/20 text-error'
                          : 'bg-success/20 text-success'
                  }`}>
                    {tvyChange > 0 ? '+' : ''}{tvyChange}% vs yesterday
                  </span>
                </div>
              )}
              {card.subItems && card.subItems.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border flex flex-wrap gap-x-3 gap-y-1" data-testid={`${card.testId}-breakdown`}>
                  {card.subItems.map((sub, idx) => (
                    <span key={idx} className={`text-[10px] ${sub.color} font-medium inline-flex items-center gap-0.5`}>
                      <span className="text-muted-foreground">{sub.label}:</span>
                      <RiyalIcon size="0.85em" />
                      {sub.value < 0 ? '-' : ''}
                      {Math.abs(sub.value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  ))}
                </div>
              )}
              {card.extraInfo && (
                <div className="mt-1.5 pt-1.5 border-t border-dashed border-border flex gap-x-4 gap-y-1 flex-wrap">
                  <span className="text-[10px] text-success font-semibold inline-flex items-center gap-0.5">
                    Received: <RiyalIcon size="0.85em" /> {(card.extraInfo.received || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                  </span>
                  {card.extraInfo.receivable > 0 && (
                    <span className="text-[10px] text-warning font-semibold inline-flex items-center gap-0.5">
                      Receivable: <RiyalIcon size="0.85em" /> {(card.extraInfo.receivable || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                    </span>
                  )}
                </div>
              )}
              {card.link && (
                <div className="mt-2 pt-1 text-[10px] text-primary font-medium">
                  View all {card.title.toLowerCase()} →
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default StatCardsGrid;
